import { useState, useEffect } from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { Calendar, Clock, ArrowRight, Search } from 'lucide-react'
import { supabase } from '../config/supabase'

export default function NewsPage() {
  const [activeCategory, setActiveCategory] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [newsItems, setNewsItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const categories = [
    { id: 'all', name: '全部' },
    { id: '公司动态', name: '公司动态' },
    { id: '产品资讯', name: '产品资讯' },
    { id: '合作新闻', name: '合作新闻' }
  ]

  useEffect(() => {
    const fetchNews = async () => {
      try {
        // 查询新闻数据
        const { data: newsData, error } = await supabase
          .from('news_s_762b1c2b_1')
          .select('*')
          .eq('is_deleted', 'n')
          .order('created_at', { ascending: false })

        if (error) {
          console.error('Error fetching news:', error)
        } else {
          // 为新闻添加默认属性，确保与原代码兼容
          const enhancedNews = (newsData || []).map((news, index) => ({
            ...news,
            id: news.id,
            title: news.title || `新闻${index + 1}`,
            date: news.date || news.created_at ? new Date(news.created_at).toISOString().split('T')[0] : '2024-01-01',
            time: news.time || '12:00',
            category: news.category || 'company',
            image: news.image || news.cover_image || `https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=${encodeURIComponent(news.title || 'news article')}&image_size=landscape_4_3`,
            summary: news.summary || news.content || '新闻内容...',
            content: news.content || news.summary || '新闻详细内容...'
          }))
          setNewsItems(enhancedNews)
        }
      } catch (error) {
        console.error('Error fetching news:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchNews()
  }, [])

  const filteredNews = newsItems.filter(item => {
    const matchesCategory = activeCategory === 'all' || item.category === activeCategory
    const matchesSearch = searchQuery === '' || item.title.toLowerCase().includes(searchQuery.toLowerCase()) || item.summary.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesCategory && matchesSearch
  })

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Page Header */}
      <section className="relative bg-gradient-to-br from-primary-700 to-primary-900 text-white py-24 lg:py-32">
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          />
        </div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-accent-orange/10 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-4">
              新闻资讯
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">新闻资讯</h1>
            <p className="text-xl text-white/80">了解云净炉业的最新动态和行业资讯</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search and Filter */}
          <div className="flex flex-col md:flex-row justify-between items-center mb-12 gap-4">
            <div className="w-full md:w-1/3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="搜索新闻..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 pl-10 rounded-lg border border-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <Search className="w-5 h-5 text-secondary-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === category.id ? 'bg-primary-600 text-white' : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'}`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* News List */}
          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
              <p className="mt-4 text-secondary-600 text-lg">加载中...</p>
            </div>
          ) : filteredNews.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-8">
              {filteredNews.map((item) => (
                <div key={item.id} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-secondary-100">
                  <div className="md:flex">
                    <div className="md:w-1/3 aspect-video md:aspect-auto">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                    </div>
                    <div className="md:w-2/3 p-6">
                      <div className="flex items-center gap-4 mb-3 text-sm text-secondary-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{item.date}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{item.time}</span>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${item.category === '公司动态' ? 'bg-blue-100 text-blue-800' : item.category === '产品资讯' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'}`}>
                          {item.category}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-secondary-900 mb-3 line-clamp-2">
                        {item.title}
                      </h3>
                      <div className="news-content text-secondary-600 mb-4 line-clamp-3">
                        <div dangerouslySetInnerHTML={{ __html: item.summary }} />
                      </div>

                      <a href={`/news-detail?id=${item.id}`} className="flex items-center gap-2 text-primary-600 font-semibold hover:text-primary-700 transition-colors">
                        <span>阅读全文</span>
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-secondary-600 text-lg">暂无新闻数据</p>
            </div>
          )}

          {/* Pagination */}
          <div className="mt-12 flex justify-center">
            <div className="inline-flex rounded-md shadow">
              <button className="px-4 py-2 border border-secondary-200 bg-white text-secondary-500 hover:bg-secondary-50 rounded-l-md">
                上一页
              </button>
              <button className="px-4 py-2 border-t border-b border-secondary-200 bg-primary-600 text-white">
                1
              </button>
              <button className="px-4 py-2 border-t border-b border-secondary-200 bg-white text-secondary-700 hover:bg-secondary-50">
                2
              </button>
              <button className="px-4 py-2 border border-secondary-200 bg-white text-secondary-500 hover:bg-secondary-50 rounded-r-md">
                下一页
              </button>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  )
}